import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Typography, Space, Card, Radio, Tag, Tooltip, Alert, Divider } from "antd";
import {
    ReloadOutlined,
    UndoOutlined,
    CameraOutlined,
    FunctionOutlined,
    BlockOutlined,
    BulbOutlined,
} from "@ant-design/icons";

const { Title, Paragraph, Text } = Typography;

/**
 * EquationBalanceScaleWidget (Flagship Implementation)
 * Upgrades:
 * - Clear "Explore vs Legal Moves" toggle (legal enforces apply-to-both-sides)
 * - Quick operations grid (+/- constants, + variable, ×/÷ scalars)
 * - Balance "physics": tilt by mass difference
 * - History snapshots + undo + reset
 * - Shows equation text + balanced badge
 */
export const EquationBalanceScaleWidget = ({ config, content }) => {
    const MASS_TOL = 0.001;
    const initialRef = useRef(null);
    const healedRef = useRef(false);

    // --- State ---
    const [leftTerms, setLeftTerms] = useState([]);
    const [rightTerms, setRightTerms] = useState([]);
    const [mode, setMode] = useState("EXPLORE"); // EXPLORE | LEGAL
    const [applyTo, setApplyTo] = useState("BOTH");
    const [history, setHistory] = useState([]);
    const [variableSymbol, setVariableSymbol] = useState("x");

    // --- Lifecycle Logic ---
    useEffect(() => {
        healedRef.current = false;
        const safeConfig = config || { xValue: 2, leftSide: [], rightSide: [] };
        const l = toUiTerms(safeConfig.leftSide || [], "L");
        const r = toUiTerms(safeConfig.rightSide || [], "R");

        const inferredSym = inferVariableSymbol([...l, ...r]) || "x";
        setVariableSymbol(inferredSym);

        setLeftTerms(l);
        setRightTerms(r);

        initialRef.current = {
            xValue: numberOr(safeConfig.xValue, 0),
            left: l,
            right: r,
            variableSymbol: inferredSym,
        };

        setHistory([]);
        setMode("EXPLORE");
        setApplyTo("BOTH");
    }, [config]);

    const xValue = numberOr(config?.xValue, initialRef.current?.xValue ?? 0);

    // --- Physics and Equation Text ---
    const leftMass = useMemo(() => calcMass(leftTerms, xValue), [leftTerms, xValue]);
    const rightMass = useMemo(() => calcMass(rightTerms, xValue), [rightTerms, xValue]);
    const diff = rightMass - leftMass;

    const tiltDeg = useMemo(() => {
        const maxTilt = 12; // degrees
        const scale = 0.15; // tuned for typical classroom numbers
        const raw = diff * scale;
        return clamp(raw, -maxTilt, maxTilt);
    }, [diff]);

    const isBalanced = Math.abs(diff) <= MASS_TOL;

    const equationText = useMemo(() => {
        return `${termsToExpression(leftTerms, variableSymbol)} = ${termsToExpression(
            rightTerms,
            variableSymbol
        )}`;
    }, [leftTerms, rightTerms, variableSymbol]);

    // --- Actions ---
    const resetAll = () => {
        const init = initialRef.current;
        if (!init) return;
        setLeftTerms(deepCopy(init.left));
        setRightTerms(deepCopy(init.right));
        setHistory([]);
        setMode("EXPLORE");
        setApplyTo("BOTH");
        if (init.variableSymbol) setVariableSymbol(init.variableSymbol);
    };

    const takeSnapshot = (label = "Action") => {
        setHistory((prev) => [...prev, { left: deepCopy(leftTerms), right: deepCopy(rightTerms), label }]);
    };

    const undoSnapshot = () => {
        setHistory((prev) => {
            if (!prev.length) return prev;
            const last = prev[prev.length - 1];
            setLeftTerms(last.left);
            setRightTerms(last.right);
            return prev.slice(0, -1);
        });
    };

    const applyQuickOp = (op) => {
        takeSnapshot(op.kind);

        const doApply = (terms) => applyOperation(terms, op);

        if (mode === "LEGAL" || applyTo === "BOTH") {
            setLeftTerms(doApply(leftTerms));
            setRightTerms(doApply(rightTerms));
        } else if (applyTo === "LEFT") {
            setLeftTerms(doApply(leftTerms));
        } else {
            setRightTerms(doApply(rightTerms));
        }
    };

    return (
        <div className="ch-eq-balance animate-slide-up">
            <Space orientation="vertical" style={{ width: "100%" }} size="middle">
                {/* Visual Header Card */}
                <Card size="small" className="ch-shapeshifter-header">
                    <div className="flexSpaceBetween flexAlignCentre">
                        <Space>
                            <Title level={4} style={{ margin: 0 }}>
                                Equation Balance Scale
                            </Title>
                            <Tag color={isBalanced ? "success" : "error"}>{isBalanced ? "Balanced" : "Unbalanced"}</Tag>
                        </Space>
                        <Button icon={<ReloadOutlined />} size="small" onClick={resetAll}>
                            Reset
                        </Button>
                    </div>

                    <Divider style={{ margin: "8px 0" }} />

                    <Paragraph style={{ marginBottom: 0 }}>
                        <Text strong>Equation:</Text>{" "}
                        <Text code style={{ fontSize: 14 }}>
                            {equationText}
                        </Text>{" "}
                        <Tag style={{ marginLeft: 8 }} color="blue">
                            x = {xValue}
                        </Tag>
                    </Paragraph>
                </Card>

                {/* Control Panel */}
                <div className="ch-eq-balance-controls">
                    <div className="flexSpaceBetween flexAlignCentre">
                        <Space size="large">
                            <Space>
                                <Text strong>Mode:</Text>
                                <Radio.Group
                                    value={mode}
                                    onChange={(e) => {
                                        setMode(e.target.value);
                                        if (e.target.value === "LEGAL") setApplyTo("BOTH");
                                    }}
                                    buttonStyle="solid"
                                >
                                    <Radio.Button value="EXPLORE">Explore</Radio.Button>
                                    <Radio.Button value="LEGAL">Legal Moves</Radio.Button>
                                </Radio.Group>
                            </Space>

                            {mode === "EXPLORE" && (
                                <Space>
                                    <Text strong>Apply to:</Text>
                                    <Radio.Group value={applyTo} onChange={(e) => setApplyTo(e.target.value)} size="small">
                                        <Radio.Button value="LEFT">Left</Radio.Button>
                                        <Radio.Button value="BOTH">Both</Radio.Button>
                                        <Radio.Button value="RIGHT">Right</Radio.Button>
                                    </Radio.Group>
                                </Space>
                            )}
                        </Space>

                        <Space>
                            <Button icon={<CameraOutlined />} onClick={() => takeSnapshot("Snapshot")}>
                                Snapshot
                            </Button>
                            <Button icon={<UndoOutlined />} onClick={undoSnapshot} disabled={!history.length}>
                                Undo
                            </Button>
                        </Space>
                    </div>

                    {/* Operation Grid */}
                    <div className="ch-eq-balance-quickOps">
                        <div className="ch-eq-balance-quickRow">
                            <Button onClick={() => applyQuickOp({ kind: "ADD_CONST", value: 1 })}>+1</Button>
                            <Button onClick={() => applyQuickOp({ kind: "ADD_CONST", value: -1 })}>−1</Button>
                            <Button onClick={() => applyQuickOp({ kind: "ADD_CONST", value: 5 })}>+5</Button>
                            <Button onClick={() => applyQuickOp({ kind: "ADD_CONST", value: -5 })}>−5</Button>
                            <Button type="primary" ghost onClick={() => applyQuickOp({ kind: "ADD_VAR", value: 1 })}>
                                +{variableSymbol}
                            </Button>

                            <Button onClick={() => applyQuickOp({ kind: "MUL", value: 2 })}>×2</Button>
                            <Button onClick={() => applyQuickOp({ kind: "DIV", value: 2 })}>÷2</Button>
                            <Button onClick={() => applyQuickOp({ kind: "MUL", value: 3 })}>×3</Button>
                            <Button onClick={() => applyQuickOp({ kind: "DIV", value: 3 })}>÷3</Button>

                            <Button
                                danger
                                ghost
                                icon={<BlockOutlined />}
                                onClick={() => {
                                    takeSnapshot("Tidy");
                                    setLeftTerms(collectLikeTerms(leftTerms));
                                    setRightTerms(collectLikeTerms(rightTerms));
                                }}
                            >
                                Tidy
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Interaction Stage */}
                <div className="ch-eq-balance-scaleWrap">
                    <div className="ch-eq-balance-scale">
                        <div className="ch-eq-balance-pivot" />

                        <div
                            className="ch-eq-balance-beam"
                            style={{ transform: `translate(-50%, -50%) rotate(${tiltDeg}deg)` }}
                        >
                            {/* Left Pan */}
                            <div
                                className="ch-eq-balance-pan ch-eq-balance-panLeft"
                                style={{ left: "15%", transform: `rotate(${-tiltDeg}deg)` }}
                            >
                                <div className="ch-eq-balance-sideTitle">LEFT</div>
                                <div className="ch-eq-balance-terms">
                                    {leftTerms.map((t) => (
                                        <div
                                            key={t.id}
                                            className={`ch-balance-block ${t.type === "VARIABLE" ? "ch-is-var" : "ch-is-const"}`}
                                        >
                                            {termLabel(t, variableSymbol)}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Right Pan */}
                            <div
                                className="ch-eq-balance-pan ch-eq-balance-panRight"
                                style={{ right: "15%", transform: `rotate(${-tiltDeg}deg)` }}
                            >
                                <div className="ch-eq-balance-sideTitle">RIGHT</div>
                                <div className="ch-eq-balance-terms">
                                    {rightTerms.map((t) => (
                                        <div
                                            key={t.id}
                                            className={`ch-balance-block ${t.type === "VARIABLE" ? "ch-is-var" : "ch-is-const"}`}
                                        >
                                            {termLabel(t, variableSymbol)}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Center Badge */}
                            <div className={`ch-eq-balance-badge ${isBalanced ? "ch-is-balanced" : "ch-is-unbalanced"}`}>
                                {isBalanced ? (
                                    <Space>
                                        <FunctionOutlined /> Balanced
                                    </Space>
                                ) : (
                                    <Space>
                                        <BlockOutlined /> Imbalanced
                                    </Space>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <Alert
                    title={
                        mode === "LEGAL"
                            ? "Legal Moves Enabled: Changes apply to both sides (keeps the equation equivalent)."
                            : "Explore Mode: Changes apply to one side to show the effect of imbalance."
                    }
                    type={mode === "LEGAL" ? "success" : "info"}
                    showIcon
                    icon={<BulbOutlined />}
                />
            </Space>
        </div>
    );
};

// ------------------------ Helpers ------------------------

function clamp(n, a, b) {
    return Math.max(a, Math.min(b, n));
}
function deepCopy(x) {
    return JSON.parse(JSON.stringify(x));
}
function numberOr(v, fallback) {
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
}
function tidyNumber(n) {
    return Math.round(n * 1000) / 1000;
}
function formatNumber(n) {
    const v = tidyNumber(n);
    return Object.is(v, -0) ? "0" : String(v);
}

function normalizeTermType(raw) {
    const s = String(raw || "").toUpperCase();
    return s === "VARIABLE" || s === "VAR" ? "VARIABLE" : "CONSTANT";
}

function toUiTerms(raw, prefix) {
    return (raw || [])
        .map((t, i) => ({
            id: `${prefix}_${Date.now()}_${i}`,
            type: normalizeTermType(t.type),
            value: numberOr(t.value, 0),
            label: t.label ?? null,
        }))
        .filter((t) => Math.abs(t.value) > 1e-9);
}

function calcMass(terms, x) {
    return (terms || []).reduce((sum, t) => (t.type === "VARIABLE" ? sum + t.value * x : sum + t.value), 0);
}

function applyOperation(terms, op) {
    const v = numberOr(op.value, 0);

    if (op.kind === "ADD_CONST") {
        return [...terms, { id: `T_${Date.now()}`, type: "CONSTANT", value: v, label: null }];
    }
    if (op.kind === "ADD_VAR") {
        return [...terms, { id: `T_${Date.now()}`, type: "VARIABLE", value: v, label: null }];
    }
    if (op.kind === "MUL") {
        return terms.map((t) => ({ ...t, value: tidyNumber(t.value * v), label: null }));
    }
    if (op.kind === "DIV") {
        if (Math.abs(v) < 1e-12) return terms;
        return terms.map((t) => ({ ...t, value: tidyNumber(t.value / v), label: null }));
    }
    return terms;
}

function collectLikeTerms(terms) {
    let vSum = 0,
        cSum = 0;
    terms.forEach((t) => (t.type === "VARIABLE" ? (vSum += t.value) : (cSum += t.value)));
    const out = [];
    if (Math.abs(vSum) > 1e-9) out.push({ id: `V_${Date.now()}`, type: "VARIABLE", value: tidyNumber(vSum) });
    if (Math.abs(cSum) > 1e-9) out.push({ id: `C_${Date.now()}`, type: "CONSTANT", value: tidyNumber(cSum) });
    return out;
}

function termLabel(t, sym) {
    // Always derive display from numeric value to keep UI consistent after operations.
    if (t.type === "VARIABLE") {
        if (t.value === 1) return sym;
        if (t.value === -1) return `−${sym}`;
        return `${formatNumber(t.value)}${sym}`;
    }
    return formatNumber(t.value);
}

function inferVariableSymbol(terms) {
    const candidates = new Set();

    (terms || []).forEach((t) => {
        if (t?.type !== "VARIABLE") return;
        const label = typeof t.label === "string" ? t.label : "";
        const m = label.match(/[A-Za-z]/g);
        if (m && m.length) candidates.add(m[0]); // single-letter pronumeral expected
    });

    if (candidates.size === 1) return [...candidates][0];
    if (candidates.size > 1) return [...candidates][0]; // fallback: pick first (validator should prevent this)
    return null;
}

function termsToExpression(terms, sym) {
    const tidy = collectLikeTerms(terms);
    if (!tidy.length) return "0";
    return tidy
        .map((t, i) => {
            const label = termLabel(t, sym);
            return i > 0 && t.value > 0 ? `+ ${label}` : label;
        })
        .join(" ");
}

export default EquationBalanceScaleWidget;
